import { rememberActiveProperty } from '@/lib/property-store';
import { PLATFORM_LABEL } from '@str/shared';
import { useQueryClient } from '@tanstack/react-query';
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type DragEvent,
} from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Check, FullScreenSpinner, Logo, StepIndicator } from '@/components/ui';
import { errorMessage, uploadFile } from '@/lib/api';
import { checkPhoto, SMALL_PHOTO_NOTE } from '@/lib/images';
import {
  keys,
  mediaApi,
  useMe,
  useProperty,
  useUpdateProperty,
  type PropertyDetail,
} from '@/lib/queries';
import { mediaStyle } from '@/lib/scenes';

export function Review() {
  const { propertyId = '' } = useParams();
  const q = useProperty(propertyId);
  const p = q.data;

  if (q.error) {
    return (
      <div className="screen">
        <div className="topbar">
          <Logo />
        </div>
        <div style={{ padding: 40 }} className="err">
          {errorMessage(q.error)}
        </div>
      </div>
    );
  }
  if (!p)
    return (
      <div className="screen">
        <div className="topbar">
          <Logo />
        </div>
        <FullScreenSpinner />
      </div>
    );
  return <ReviewBody key={p.id} p={p} />;
}

type Drawer = 'photos' | 'description' | 'reviews';
const DESCRIPTION_DEBOUNCE_MS = 600;
type DescriptionSave = { state: 'idle' | 'saving' | 'saved' } | { state: 'error'; message: string };
type Review = PropertyDetail['reviews'][number];

/** Photos after the hero that go into the feed: the "6 most postable". */
const FEED_PICKS = 6;
const FILMSTRIP = 8;
const REVIEW_PREVIEW = 3;
const AVATAR_TINTS = ['#5c4b70', '#3a6a86', '#6a4a33', '#4a5a4d', '#8b3c1a', '#3f6d3b'];

const smallCheck: CSSProperties = { flex: 'none' };
const cardLink: CSSProperties = { color: '#111', fontWeight: 600, whiteSpace: 'nowrap' };

function ReviewBody({ p }: { p: PropertyDetail }) {
  const navigate = useNavigate();
  const me = useMe();
  const update = useUpdateProperty(p.id);
  const [description, setDescription] = useState(p.description);
  // The last text the server confirmed, and how the latest save went: the drawer says so.
  const [savedDescription, setSavedDescription] = useState(p.description);
  const [descriptionSave, setDescriptionSave] = useState<DescriptionSave>({ state: 'idle' });
  const [drawer, setDrawer] = useState<Drawer | null>(null);

  const loggedIn = !!me.data?.user;
  const hasPlan = !!me.data?.subscription?.active;
  const fromApp = loggedIn && p.status === 'active';
  // Imported photos plus anything the host added; the order is the feed order.
  const listing = p.media.filter(
    (m) => m.kind === 'photo' && (m.source === 'listing' || m.source === 'uploaded'),
  );
  // What the import brought in, against what the listing had; uploads are counted apart, so a
  // library that grew never reads as "5 of 3".
  const imported = Math.min(listing.filter((m) => m.source === 'listing').length, p.photoCount);
  const added = listing.filter((m) => m.source === 'uploaded').length;
  const hero = listing[0];
  const sourceLabel = p.platform === 'direct' ? 'your site' : PLATFORM_LABEL[p.platform];
  const wordCount = description.trim().split(/\s+/).filter(Boolean).length;
  const quotable = p.reviews.filter((r) => r.quotable);
  const previewReviews = [...quotable, ...p.reviews.filter((r) => !r.quotable)].slice(
    0,
    REVIEW_PREVIEW,
  );
  const strip = listing.slice(3, 3 + FILMSTRIP);
  const beyondStrip = Math.max(0, listing.length - 3 - FILMSTRIP + 1);

  const descriptionDirty = description.trim() !== savedDescription.trim();
  // The description saves as the host types (debounced) and is flushed on every way out: Save,
  // close, Escape, the backdrop, the tabs, leaving the screen and a reload. The request is
  // keepalive (useUpdateProperty), so a reload right after closing cannot abort it. Refs hold
  // what the server confirmed and what is in flight, because mutation callbacks and the
  // pagehide listener run outside a render.
  const confirmedRef = useRef(p.description);
  const inFlightRef = useRef<string | null>(null);
  const seqRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const sendDescription = (mode: 'flush' | 'debounced') => {
    clearTimeout(timerRef.current);
    const text = description;
    if (text.trim() === (inFlightRef.current ?? confirmedRef.current).trim()) return;
    // A typed save waits for the one in flight (its answer sends the rest); a flush never waits.
    if (mode === 'debounced' && inFlightRef.current !== null) return;
    const seq = ++seqRef.current;
    inFlightRef.current = text;
    setDescriptionSave({ state: 'saving' });
    update.mutate(
      { description: text },
      {
        // Only the latest request's answer counts; "Saved" means the server has this text.
        onSuccess: (data) => {
          if (seq !== seqRef.current) return;
          inFlightRef.current = null;
          confirmedRef.current = data.description;
          setSavedDescription(data.description);
          setDescriptionSave({ state: 'saved' });
          sendLatestRef.current('debounced');
        },
        onError: (e) => {
          if (seq !== seqRef.current) return;
          inFlightRef.current = null;
          setDescriptionSave({ state: 'error', message: errorMessage(e) });
          // A failed save never fails quietly: bring the drawer back with the reason.
          setDrawer('description');
        },
      },
    );
  };
  const sendLatestRef = useRef(sendDescription);
  useLayoutEffect(() => {
    sendLatestRef.current = sendDescription;
  });
  const saveDescription = () => sendDescription('flush');
  useEffect(() => {
    timerRef.current = setTimeout(
      () => sendLatestRef.current('debounced'),
      DESCRIPTION_DEBOUNCE_MS,
    );
    return () => clearTimeout(timerRef.current);
  }, [description]);
  useEffect(() => {
    const flush = () => sendLatestRef.current('flush');
    const onHidden = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', onHidden);
    return () => {
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', onHidden);
      flush(); // leaving the screen inside the app
    };
  }, []);
  const closeDrawer = () => {
    setDrawer(null);
    saveDescription();
  };
  const switchDrawer = (t: Drawer) => {
    saveDescription();
    setDrawer(t);
  };
  const toggleDetail = (label: string) => {
    update.mutate({
      details: p.details.map((d) => (d.label === label ? { ...d, enabled: !d.enabled } : d)),
    });
  };
  const next = () => {
    if (!loggedIn) return void navigate(`/signup?property=${p.id}`);
    if (!hasPlan || (p.role === 'owner' && !p.plan))
      return void navigate(`/onboarding/plan?property=${p.id}`);
    rememberActiveProperty(p.id);
    void navigate('/app/create');
  };

  return (
    <div className="screen" style={{ position: 'relative' }}>
      <div className="topbar">
        <Logo to={fromApp ? '/app' : '/'} />
        {fromApp ? (
          <Link to="/app" style={{ fontSize: 13, fontWeight: 600 }}>
            ← Back to the app
          </Link>
        ) : (
          <StepIndicator current={2} />
        )}
      </div>
      <div
        style={{
          maxWidth: 1120,
          width: '100%',
          margin: '0 auto',
          padding: '0 40px 60px',
          display: 'flex',
          flexDirection: 'column',
          gap: 22,
        }}
      >
        <div
          className="a-rise-5"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            gap: 16,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
            <h1 style={{ fontSize: 38, fontWeight: 800, letterSpacing: '-.035em' }}>
              Here&apos;s <em style={{ fontStyle: 'normal', color: 'var(--accent)' }}>{p.name}</em>,
              as we see it
            </h1>
            <p style={{ fontSize: 15, color: '#666' }}>
              Edit anything. This is the brief for every post we write.
            </p>
            {p.profile?.archetype && (
              <p data-testid="profile-line" style={{ fontSize: 14, color: '#555' }}>
                Looks like {/^[aeiou]/i.test(p.profile.archetype) ? 'an' : 'a'}{' '}
                <b>{p.profile.archetype}</b>
                {p.profile.guests ? ` for ${p.profile.guests}` : ''}. Every post gets written for
                that.
              </p>
            )}
            {/* Trust line: what we read, from where, and a way to check it against the source. */}
            <div
              data-testid="source-line"
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: '6px 14px',
                fontSize: 12.5,
                color: '#777',
                marginTop: 4,
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Check size={14} style={smallCheck} />
                {imported} of {p.photoCount} listing photos saved
                {added ? ` · ${added} more you added` : ''}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Check size={14} style={smallCheck} />
                {p.description ? 'Description saved word for word' : 'No description found'}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Check
                  size={14}
                  style={p.reviews.length ? smallCheck : { ...smallCheck, opacity: 0.35 }}
                />
                {p.reviews.length} of {p.reviewCount} reviews read
              </span>
              {p.sourceUrl && (
                <a
                  href={p.sourceUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="tap-link"
                  style={{ color: '#555', fontWeight: 600 }}
                >
                  Compare with {sourceLabel} ↗
                </a>
              )}
            </div>
          </div>
          <button className="btn btn-primary btn-lg hv" onClick={next}>
            {fromApp ? 'Done →' : 'Looks right →'}
          </button>
        </div>

        <div
          className="grid-md-1"
          style={{
            display: 'grid',
            gridTemplateColumns: '1.3fr 1fr',
            gap: 20,
            animation: 'rise .5s both',
            animationDelay: '.1s',
            alignItems: 'start',
          }}
        >
          {/* Photos: hero trio, a filmstrip, and the drawer for everything else. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr',
                gridTemplateRows: '1fr 1fr',
                gap: 10,
                height: 400,
              }}
            >
              <button
                type="button"
                onClick={() => setDrawer('photos')}
                aria-label="Open all photos"
                className="hv"
                style={{
                  gridRow: '1/3',
                  border: 0,
                  padding: 0,
                  borderRadius: 18,
                  overflow: 'hidden',
                  position: 'relative',
                  cursor: 'pointer',
                  ...mediaStyle(hero ?? { scene: p.heroScene }),
                }}
              >
                <span className="tag" style={{ position: 'absolute', top: 12, left: 12 }}>
                  Hero
                </span>
              </button>
              <div
                style={{
                  borderRadius: 14,
                  overflow: 'hidden',
                  ...mediaStyle(listing[1] ?? { scene: 'deck' }),
                }}
              />
              <div
                style={{
                  borderRadius: 14,
                  overflow: 'hidden',
                  ...mediaStyle(listing[2] ?? { scene: 'living' }),
                }}
              />
            </div>
            {strip.length > 0 && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${FILMSTRIP}, 1fr)`,
                  gap: 6,
                }}
              >
                {strip.map((m, i) => {
                  const last = i === strip.length - 1 && beyondStrip > 1;
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setDrawer('photos')}
                      aria-label={last ? `${beyondStrip} more photos` : m.label}
                      className="hv lift-1"
                      style={{
                        aspectRatio: '1',
                        border: 0,
                        padding: 0,
                        borderRadius: 9,
                        position: 'relative',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontSize: 13,
                        fontWeight: 700,
                        ...mediaStyle(m),
                      }}
                    >
                      {last && (
                        <>
                          <span
                            style={{
                              position: 'absolute',
                              inset: 0,
                              background: 'rgba(0,0,0,.45)',
                            }}
                          />
                          <span style={{ position: 'relative' }}>+{beyondStrip}</span>
                        </>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
                fontSize: 12.5,
                color: '#777',
              }}
            >
              <span>
                We picked the hero and the {Math.min(FEED_PICKS, Math.max(0, listing.length - 1))}{' '}
                most postable. Open the photos to choose a different hero.
              </span>
              <button
                type="button"
                className="hv"
                onClick={() => setDrawer('photos')}
                style={{ ...cardLink, border: 0, background: 'none', padding: 0, fontSize: 12.5 }}
              >
                See all {listing.length} photos →
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
            {/* Description: a five-line preview, edited in full inside the drawer. */}
            <div
              className="card card-pad"
              style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ fontWeight: 700 }}>
                  Your description{' '}
                  <span style={{ color: '#999', fontWeight: 500 }}>· from {sourceLabel}</span>
                </span>
                <span style={{ color: '#999', fontSize: 12 }}>{wordCount} words</span>
              </div>
              <div style={{ position: 'relative' }}>
                <div
                  style={{
                    fontSize: 14,
                    color: '#444',
                    lineHeight: 1.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 5,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    minHeight: 21,
                  }}
                >
                  {description || 'Tell guests what the place is like, in your words.'}
                </div>
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: 34,
                    background: 'linear-gradient(rgba(255,255,255,0), var(--surface))',
                    pointerEvents: 'none',
                  }}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 12,
                  fontSize: 12.5,
                  color: '#777',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Check size={16} style={smallCheck} />
                  Posts use your words, not ours.
                </span>
                <button
                  type="button"
                  className="hv"
                  onClick={() => setDrawer('description')}
                  style={{ ...cardLink, border: 0, background: 'none', padding: 0, fontSize: 12.5 }}
                >
                  Read &amp; edit →
                </button>
              </div>
            </div>

            <div
              className="card card-pad"
              style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
            >
              <div style={{ fontSize: 13, fontWeight: 700 }}>
                Details we&apos;ll use{' '}
                <span style={{ color: '#999', fontWeight: 500 }}>· tap to remove</span>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {p.details.map((d) => (
                  <button
                    key={d.label}
                    type="button"
                    className="hv lift-1"
                    onClick={() => toggleDetail(d.label)}
                    aria-pressed={d.enabled}
                    style={{
                      border: 0,
                      padding: '6px 11px',
                      borderRadius: 99,
                      background: d.enabled ? '#f4f2ed' : 'transparent',
                      color: d.enabled ? '#111' : '#bbb',
                      fontSize: 12.5,
                      fontWeight: 500,
                      textDecoration: d.enabled ? 'none' : 'line-through',
                    }}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Reviews: the most quotable lines, one line each; the drawer has all of them. */}
            <div
              className="card card-pad"
              style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ fontWeight: 700 }}>
                  Quotable reviews{' '}
                  <span style={{ color: '#999', fontWeight: 500 }}>
                    {p.reviews.length
                      ? `· from ${p.reviews.length}${p.reviewCount > p.reviews.length ? ` of ${p.reviewCount}` : ''}`
                      : p.reviewCount
                        ? `· ${p.reviewCount} on ${sourceLabel}`
                        : ''}
                  </span>
                </span>
                {!!p.rating && <span style={{ color: '#999' }}>★ {p.rating.toFixed(2)}</span>}
              </div>
              {p.reviews.length === 0 && (
                <p style={{ fontSize: 13, color: '#777', lineHeight: 1.45, margin: 0 }}>
                  Add your favourite guest quotes to any post from the composer.
                </p>
              )}
              {previewReviews.map((r, i) => (
                <div
                  key={r.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 12,
                    alignItems: 'center',
                  }}
                >
                  <span
                    style={{
                      fontSize: 13.5,
                      lineHeight: 1.45,
                      color: '#333',
                      display: '-webkit-box',
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      minWidth: 0,
                    }}
                  >
                    “{r.quote}”
                  </span>
                  <span
                    style={{
                      color: '#999',
                      whiteSpace: 'nowrap',
                      fontSize: 12,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      flex: 'none',
                    }}
                  >
                    <Avatar review={r} index={i} size={20} />
                    {r.author}
                  </span>
                </div>
              ))}
              {p.reviews.length > 0 && (
                <button
                  type="button"
                  className="hv"
                  onClick={() => setDrawer('reviews')}
                  style={{
                    ...cardLink,
                    alignSelf: 'flex-start',
                    border: 0,
                    background: 'none',
                    padding: 0,
                    fontSize: 12.5,
                  }}
                >
                  Read all {p.reviews.length} →
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {drawer && (
        <ReviewDrawer
          p={p}
          listing={listing}
          tab={drawer}
          onTab={switchDrawer}
          onClose={closeDrawer}
          description={description}
          onDescription={(v) => {
            setDescription(v);
            if (descriptionSave.state !== 'saving') setDescriptionSave({ state: 'idle' });
          }}
          onSaveDescription={saveDescription}
          descriptionDirty={descriptionDirty}
          descriptionSave={descriptionSave}
          onHero={(id) => update.mutate({ heroMediaId: id })}
          sourceLabel={sourceLabel}
          onboarding={!loggedIn}
        />
      )}
    </div>
  );
}

function Avatar({ review, index, size }: { review: Review; index: number; size: number }) {
  if (review.avatarUrl)
    return (
      <img
        src={review.avatarUrl}
        alt={review.author}
        width={size}
        height={size}
        loading="lazy"
        referrerPolicy="no-referrer"
        style={{ borderRadius: 99, objectFit: 'cover', flex: 'none' }}
      />
    );
  return (
    <span
      aria-hidden
      style={{
        width: size,
        height: size,
        borderRadius: 99,
        background: AVATAR_TINTS[index % AVATAR_TINTS.length],
        color: '#fff',
        fontSize: Math.round(size * 0.42),
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 'none',
      }}
    >
      {review.author.slice(0, 1).toUpperCase()}
    </span>
  );
}

function ReviewDrawer({
  p,
  listing,
  tab,
  onTab,
  onClose,
  description,
  onDescription,
  onSaveDescription,
  descriptionDirty,
  descriptionSave,
  onHero,
  sourceLabel,
  onboarding,
}: {
  p: PropertyDetail;
  listing: PropertyDetail['media'];
  tab: Drawer;
  onTab: (t: Drawer) => void;
  onClose: () => void;
  description: string;
  onDescription: (v: string) => void;
  onSaveDescription: () => void;
  descriptionDirty: boolean;
  descriptionSave: DescriptionSave;
  onHero: (mediaId: string) => void;
  sourceLabel: string;
  /** Before sign-up the upload goes through the onboarding session endpoint. */
  onboarding: boolean;
}) {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<'quotable' | 'all'>('quotable');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: keys.property(p.id) });

  const addFiles = async (files: FileList | File[]) => {
    const list = Array.from(files).filter((f) => f.type.startsWith('image/'));
    if (!list.length) return;
    setError(null);
    setBusy(`Adding ${list.length} photo${list.length > 1 ? 's' : ''}…`);
    try {
      let small = false;
      for (const f of list) {
        const check = await checkPhoto(f);
        if (!check.ok) {
          setError(check.reason);
          continue;
        }
        small ||= check.small;
        await uploadFile(p.id, f, { onboarding });
      }
      await refresh();
      if (small) setError(SMALL_PHOTO_NOTE);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };
  const removePhoto = async (id: string) => {
    setError(null);
    try {
      await mediaApi.remove(id);
      await refresh();
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files.length) void addFiles(e.dataTransfer.files);
  };
  const quotable = p.reviews.filter((r) => r.quotable);
  const shown = filter === 'quotable' && quotable.length ? quotable : p.reviews;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const tabs: { key: Drawer; label: string }[] = [
    { key: 'photos', label: `Photos · ${listing.length}` },
    { key: 'description', label: 'Description' },
    { key: 'reviews', label: `Reviews · ${p.reviews.length}` },
  ];
  const heading: Record<Drawer, [string, string]> = {
    photos: [
      'Every photo we saved',
      `The hero and the next ${Math.min(FEED_PICKS, Math.max(0, listing.length - 1))} go in the feed. Make any photo the hero, delete the ones you don't want, or add your own.`,
    ],
    description: [
      'Your description, word for word',
      `${description.trim().split(/\s+/).filter(Boolean).length} words from ${sourceLabel}. Every post is written in this voice.`,
    ],
    reviews: [
      `All ${p.reviews.length} reviews`,
      'Lines marked Quotable can appear on posts, credited by first name.',
    ],
  };

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(20,18,14,.35)',
          backdropFilter: 'blur(2px)',
          animation: 'fade .25s both',
          zIndex: 20,
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={heading[tab][0]}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 'min(620px, 100vw)',
          background: '#fff',
          boxShadow: '-30px 0 80px -30px rgba(0,0,0,.35)',
          display: 'flex',
          flexDirection: 'column',
          animation: 'slide-in .35s cubic-bezier(.2,.8,.2,1) both',
          zIndex: 21,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '18px 22px 0',
            gap: 12,
          }}
        >
          <div className="seg round" role="tablist" aria-label="Sections">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                className={`seg-item hv${tab === t.key ? ' on' : ''}`}
                onClick={() => onTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="hv"
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 34,
              height: 34,
              borderRadius: 99,
              border: 0,
              background: 'transparent',
              fontSize: 16,
              color: '#555',
              cursor: 'pointer',
            }}
          >
            ✕
          </button>
        </div>
        <div style={{ padding: '22px 22px 0', display: 'flex', flexDirection: 'column', gap: 4 }}>
          <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, letterSpacing: '-.03em' }}>
            {heading[tab][0]}
          </h2>
          <p style={{ margin: 0, fontSize: 13.5, color: '#777' }}>{heading[tab][1]}</p>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '18px 22px 30px' }}>
          {tab === 'photos' && (
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                borderRadius: 14,
                outline: dragging ? '2px dashed var(--accent)' : '2px dashed transparent',
                outlineOffset: 6,
                transition: 'outline-color .15s',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  fontSize: 12.5,
                  color: '#777',
                }}
              >
                <span style={{ flex: 1, minWidth: 0, color: error ? 'var(--accent)' : undefined }}>
                  {busy ?? error ?? 'Drop photos anywhere here, or'}
                </span>
                <button
                  type="button"
                  className="btn btn-outline hv"
                  disabled={!!busy}
                  onClick={() => fileInput.current?.click()}
                  style={{
                    flex: 'none',
                    width: 'auto',
                    borderRadius: 999,
                    padding: '7px 14px',
                    fontSize: 12.5,
                  }}
                >
                  + Add photos
                </button>
                <input
                  ref={fileInput}
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  aria-label="Add photos"
                  onChange={(e) => {
                    if (e.target.files) void addFiles(e.target.files);
                    e.target.value = '';
                  }}
                />
              </div>
              <div
                data-testid="all-photos"
                style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}
              >
                {listing.map((m, i) => {
                  const isHero = i === 0;
                  const tag = isHero ? 'Hero' : i <= FEED_PICKS ? 'Feed pick' : null;
                  return (
                    <div
                      key={m.id}
                      title={m.label}
                      style={{
                        aspectRatio: '1',
                        borderRadius: 12,
                        position: 'relative',
                        overflow: 'hidden',
                        animation: 'pop .4s both',
                        animationDelay: `${Math.min(i, 14) * 0.03}s`,
                        boxShadow: isHero ? 'inset 0 0 0 2.5px #111' : 'none',
                        ...mediaStyle(m),
                      }}
                    >
                      {tag && (
                        <span
                          className="tag tag-sm"
                          style={{ position: 'absolute', top: 8, left: 8, pointerEvents: 'none' }}
                        >
                          {tag}
                        </span>
                      )}
                      <span
                        style={{
                          position: 'absolute',
                          bottom: 8,
                          right: 9,
                          fontSize: 11,
                          fontWeight: 600,
                          color: 'rgba(255,255,255,.85)',
                          pointerEvents: 'none',
                        }}
                      >
                        {i + 1}
                      </span>
                      {!isHero && (
                        <button
                          type="button"
                          className="hv"
                          onClick={() => onHero(m.id)}
                          aria-label={`Set as hero: ${m.label}`}
                          style={{
                            position: 'absolute',
                            bottom: 8,
                            left: 8,
                            fontSize: 11,
                            fontWeight: 700,
                            color: '#111',
                            background: 'rgba(255,255,255,.75)',
                            border: 0,
                            padding: '4px 9px',
                            borderRadius: 99,
                            cursor: 'pointer',
                            backdropFilter: 'blur(6px)',
                          }}
                        >
                          Set as hero
                        </button>
                      )}
                      <button
                        type="button"
                        className="hv"
                        onClick={() => void removePhoto(m.id)}
                        aria-label={`Delete ${m.label}`}
                        title="Delete this photo"
                        style={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          width: 22,
                          height: 22,
                          borderRadius: 99,
                          border: 0,
                          background: 'rgba(0,0,0,.5)',
                          color: '#fff',
                          fontSize: 12,
                          lineHeight: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          backdropFilter: 'blur(6px)',
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {tab === 'description' && (
            <>
              <textarea
                value={description}
                onChange={(e) => onDescription(e.target.value)}
                rows={26}
                aria-label="Description"
                placeholder="Tell guests what the place is like, in your words."
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  border: '1px solid rgba(0,0,0,.08)',
                  borderRadius: 14,
                  padding: '16px 18px',
                  fontSize: 14.5,
                  color: '#333',
                  lineHeight: 1.6,
                  resize: 'none',
                  background: 'var(--bg)',
                  fontFamily: 'inherit',
                }}
              />
              <div
                style={{
                  marginTop: 12,
                  fontSize: 12.5,
                  color: '#777',
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                }}
              >
                <Check size={16} style={smallCheck} />
                Edits here change how every post is written. Your {sourceLabel} listing is
                untouched.
              </div>
              <div
                style={{
                  marginTop: 14,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 12,
                  fontSize: 12.5,
                }}
              >
                <span
                  role="status"
                  style={{
                    flex: 1,
                    minWidth: 0,
                    color: descriptionSave.state === 'error' ? 'var(--accent)' : '#777',
                  }}
                >
                  {descriptionSave.state === 'saving'
                    ? 'Saving…'
                    : descriptionSave.state === 'error'
                      ? `Not saved: ${descriptionSave.message}`
                      : descriptionDirty
                        ? 'Unsaved changes'
                        : descriptionSave.state === 'saved'
                          ? 'Saved'
                          : ''}
                </span>
                <button
                  type="button"
                  className="btn btn-primary hv"
                  disabled={!descriptionDirty || descriptionSave.state === 'saving'}
                  onClick={onSaveDescription}
                  style={{ flex: 'none', width: 'auto', padding: '8px 18px' }}
                >
                  Save
                </button>
              </div>
            </>
          )}
          {tab === 'reviews' && (
            <>
              <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                {(
                  [
                    ['quotable', `Quotable · ${quotable.length}`],
                    ['all', `All ${p.reviews.length}`],
                  ] as const
                ).map(([key, label]) => {
                  const on = filter === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      className="hv"
                      aria-pressed={on}
                      onClick={() => setFilter(key)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: 99,
                        fontSize: 12.5,
                        fontWeight: 600,
                        background: on ? '#111' : 'transparent',
                        color: on ? '#fff' : '#555',
                        border: `1px solid ${on ? '#111' : 'rgba(0,0,0,.12)'}`,
                        cursor: 'pointer',
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {shown.map((r, i) => (
                  <div
                    key={r.id}
                    style={{
                      display: 'flex',
                      gap: 14,
                      padding: '16px 0',
                      borderBottom: '1px solid rgba(0,0,0,.06)',
                    }}
                  >
                    <Avatar review={r} index={i} size={34} />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: 1 }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: 12.5,
                          gap: 12,
                        }}
                      >
                        <span style={{ fontWeight: 700 }}>{r.author}</span>
                        <span style={{ color: '#999' }}>{r.reviewedAt}</span>
                      </div>
                      <div style={{ fontSize: 14, lineHeight: 1.5, color: '#333' }}>{r.quote}</div>
                      {r.quotable && (
                        <span
                          style={{
                            alignSelf: 'flex-start',
                            fontSize: 11,
                            fontWeight: 700,
                            color: 'var(--accent)',
                            background: 'var(--accent-tint)',
                            padding: '3px 8px',
                            borderRadius: 99,
                          }}
                        >
                          Quotable
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
